import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';


const HistoricalData = ({ historicalIncidents, trendData }) => {
  const [viewType, setViewType] = useState('table');
  const [timeRange, setTimeRange] = useState('30d');

  const timeRanges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-error bg-error/10';
      case 'major':
        return 'text-warning bg-warning/10';
      case 'minor':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return 'AlertTriangle';
      case 'major':
        return 'AlertCircle';
      case 'minor':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-foreground">Historical Data & Trends</h3>
            <p className="text-sm text-muted-foreground">Past incidents and service reliability patterns</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Time Range Selector */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {timeRanges?.map((range) => (
                <button
                  key={range?.value}
                  onClick={() => setTimeRange(range?.value)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    timeRange === range?.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {range?.label}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewType === 'table' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Table" size={16} />
              </button>
              <button
                onClick={() => setViewType('chart')}
                className={`p-2 rounded-md transition-colors ${
                  viewType === 'chart' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="BarChart3" size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {viewType === 'table' ? (
          /* Table View */
          (<div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Severity</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Duration</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Reports</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Affected Areas</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {historicalIncidents?.map((incident) => (
                  <tr key={incident?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-foreground">{incident?.date}</p>
                        <p className="text-sm text-muted-foreground">{incident?.time}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-sm ${getSeverityColor(incident?.severity)}`}>
                        <Icon name={getSeverityIcon(incident?.severity)} size={14} />
                        <span className="capitalize">{incident?.severity}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-foreground">{incident?.duration}</td>
                    <td className="py-3 px-4 text-foreground">{incident?.reports?.toLocaleString()}</td>
                    <td className="py-3 px-4 text-foreground">{incident?.affectedAreas}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 text-success">
                        <Icon name="CheckCircle" size={14} />
                        <span className="text-sm">Resolved</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>)
        ) : (
          /* Chart View */
          (<div className="space-y-8">
            {/* Outage Frequency Chart */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Outage Frequency Over Time</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData?.frequency}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="incidents" 
                      stroke="var(--color-primary)" 
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Severity Distribution */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Incidents by Severity</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData?.severity}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="severity" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>)
        )}

        {/* Summary Stats */}
        <div className="mt-8 pt-6 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">98.7%</p>
              <p className="text-sm text-muted-foreground">Uptime (30 days)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">4.2h</p>
              <p className="text-sm text-muted-foreground">Avg Resolution Time</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">12</p>
              <p className="text-sm text-muted-foreground">Total Incidents</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-foreground">847K</p>
              <p className="text-sm text-muted-foreground">Users Affected</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalData;